import { NotificationOut } from '../api/types';

export interface FormattedNotification {
  title: string;
  message: string;
}

export function formatNotification(n: NotificationOut, locale: 'de' | 'en' | 'tr' | string): FormattedNotification {
  const p = n.payload_json || {};
  const kind = n.kind || '';

  const isDe = locale === 'de';
  const isTr = locale === 'tr';

  const fmtCurrency = (val: any) => {
    if (val === undefined || val === null) return '0,00 €';
    const num = typeof val === 'number' ? val : parseFloat(String(val));
    if (isNaN(num)) return '0,00 €';
    return isDe || isTr
      ? `${num.toFixed(2).replace('.', ',')} €`
      : `€${num.toFixed(2)}`;
  };

  const ticketIdShort = typeof p.ticket_id === 'string' ? p.ticket_id.slice(-6) : '';

  switch (kind) {
    case 'bet_accepted': {
      const stakeStr = fmtCurrency(p.stake);
      const payoutStr = fmtCurrency(p.potential_payout);
      let title = 'Bet Accepted';
      let message = `Your bet (ID: ...${ticketIdShort}) has been accepted. Stake: ${stakeStr}, Potential Payout: ${payoutStr}.`;
      
      if (isTr) {
        title = 'Bahis Kabul Edildi';
        message = `Bahis kuponunuz (ID: ...${ticketIdShort}) kabul edildi. Yatırılan Tutar: ${stakeStr}, Olası Kazanç: ${payoutStr}.`;
      } else if (isDe) {
        title = 'Wette angenommen';
        message = `Ihre Wette (ID: ...${ticketIdShort}) wurde angenommen. Einsatz: ${stakeStr}, Möglicher Gewinn: ${payoutStr}.`;
      }
      return { title, message };
    }
    
    case 'bet_won': {
      const payoutStr = fmtCurrency(p.payout || p.amount || p.winnings);
      let title = 'Bet Won';
      let message = `Congratulations! Your bet (ID: ...${ticketIdShort}) won. Payout: ${payoutStr}.`;
      
      if (isTr) {
        title = 'Bahis Kazandı';
        message = `Tebrikler! Kuponunuz (ID: ...${ticketIdShort}) kazandı. Toplam Kazanç: ${payoutStr}.`;
      } else if (isDe) {
        title = 'Wette gewonnen';
        message = `Glückwunsch! Ihre Wette (ID: ...${ticketIdShort}) hat gewonnen. Gewinn: ${payoutStr}.`;
      }
      return { title, message };
    }

    case 'bet_lost': {
      let title = 'Bet Lost';
      let message = `Your bet (ID: ...${ticketIdShort}) lost.`;
      
      if (isTr) {
        title = 'Bahis Kaybetti';
        message = `Kuponunuz (ID: ...${ticketIdShort}) maalesef kaybetti.`;
      } else if (isDe) {
        title = 'Wette verloren';
        message = `Ihre Wette (ID: ...${ticketIdShort}) hat leider verloren.`;
      }
      return { title, message };
    }

    case 'wallet_deposit': {
      const amountStr = fmtCurrency(p.amount);
      let title = 'Deposit Successful';
      let message = `Your deposit of ${amountStr} was successful.`;
      
      if (isTr) {
        title = 'Para Yatırma Başarılı';
        message = `${amountStr} tutarındaki para yatırma işleminiz başarıyla tamamlandı.`;
      } else if (isDe) {
        title = 'Einzahlung erfolgreich';
        message = `Ihre Einzahlung von ${amountStr} war erfolgreich.`;
      }
      return { title, message };
    }

    case 'wallet_withdrawal': {
      const amountStr = fmtCurrency(p.amount);
      let title = 'Withdrawal Requested';
      let message = `Your withdrawal of ${amountStr} has been requested.`;
      
      if (isTr) {
        title = 'Para Çekme Talebi İletildi';
        message = `${amountStr} tutarındaki para çekme talebiniz işleme alındı.`;
      } else if (isDe) {
        title = 'Auszahlung beantragt';
        message = `Ihre Auszahlung von ${amountStr} wurde erfolgreich beantragt.`;
      }
      return { title, message };
    }

    case 'kyc_approved':
    case 'account_verified': {
      let title = 'Account Verified';
      let message = 'Your identity has been successfully verified. You can now use all features of your account.';
      if (isTr) {
        title = 'Hesap Doğrulandı';
        message = 'Kimliğiniz başarıyla doğrulandı. Artık hesabınızın tüm özelliklerini kullanabilirsiniz.';
      } else if (isDe) {
        title = 'Konto verifiziert';
        message = 'Ihre Identität wurde erfolgreich verifiziert. Sie können nun alle Funktionen nutzen.';
      }
      return { title, message };
    }

    case 'bonus_awarded': {
      const amountStr = fmtCurrency(p.amount || p.bonus_amount);
      let title = 'Bonus Awarded';
      let message = `You have received a bonus of ${amountStr}!`;
      if (isTr) {
        title = 'Bonus Eklendi';
        message = `Hesabınıza ${amountStr} bonus eklendi!`;
      } else if (isDe) {
        title = 'Bonus erhalten';
        message = `Sie haben einen Bonus von ${amountStr} erhalten!`;
      }
      return { title, message };
    }

    default: {
      // Clean up the title from snake_case
      let rawTitle = p.title || n.kind || 'System Notification';
      let title = String(rawTitle).replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      // Translate generic titles if possible
      if (isTr && title === 'System Notification') title = 'Sistem Bildirimi';
      if (isDe && title === 'System Notification') title = 'Systembenachrichtigung';

      // If there's an explicit text message, just use it
      if (p.message || p.body || p.description || p.text) {
         return { title, message: String(p.message || p.body || p.description || p.text) };
      }

      // Otherwise, map the JSON keys to a human-readable list
      let msgParts = [];
      const keys = Object.keys(p);
      
      if (keys.length === 0) {
        let emptyMsg = 'You have a new notification.';
        if (isTr) emptyMsg = 'Yeni bir bildiriminiz var.';
        if (isDe) emptyMsg = 'Sie haben eine neue Benachrichtigung.';
        return { title, message: emptyMsg };
      }

      for (const [key, value] of Object.entries(p)) {
        if (typeof value === 'object' || value === null || value === undefined) continue;
        const cleanKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        msgParts.push(`${cleanKey}: ${value}`);
      }
      
      const message = msgParts.join(' • ');
      return { title, message };
    }
  }
}
